import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Typography,
} from "@mui/material";
import { Formik, Form } from "formik";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";
import * as Yup from "yup";

import {
  PurchaseTenDlcPhoneNumbersMutation,
  GetTenDlcCampaignsForSubaccountQuery,
} from "~frontend/__generated__/graphql";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import FormikErrorBanner, {
  submitErrorField,
} from "~frontend/components/forms/FormikErrorBanner";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import { plural } from "~frontend/util/plural";
import splitCommaSeparatedList from "~frontend/util/splitCommaSeparatedList";

const DEFAULT_MAX = 49;
const NOTIFICATION_DURATION = 20000;

export function Purchase10DlcPhoneNumberDialog({
  open,
  onClose,
  onSubmit,
  availableCampaigns,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: {
    areaCodes: string[];
    quantity: number;
    tcrCampaignId: string;
  }) => Promise<
    PurchaseTenDlcPhoneNumbersMutation["purchaseTenDlcPhoneNumbers"]
  >;
  availableCampaigns: GetTenDlcCampaignsForSubaccountQuery["tenDlcCampaignsForSubaccount"];
}): ReactElement {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <Formik
        initialValues={{
          [submitErrorField]: null,
          areaCodes: "",
          quantity: 1,
          tcrCampaignId:
            availableCampaigns.length === 1
              ? availableCampaigns[0].tcrCampaignId
              : "",
        }}
        validationSchema={Yup.object().shape({
          [submitErrorField]: Yup.string().nullable(),
          areaCodes: Yup.string()
            .test(
              "areaCodes",
              "Must be a comma-separated list of area codes",
              value => {
                if (!value) {
                  return false;
                }

                const areaCodes = splitCommaSeparatedList(value);
                return areaCodes.every(areaCode => /^\d{3}$/.test(areaCode));
              }
            )
            .required("At least one area code is required"),
          quantity: Yup.number()
            .min(1, "Quantity must be at least 1")
            .max(2500, "Quantity must be at most 2500"),
          tcrCampaignId: Yup.string().required("10DLC Campaign ID is required"),
        })}
        onSubmit={async (values, formik) => {
          try {
            const numbers = await onSubmit({
              tcrCampaignId: values.tcrCampaignId,
              quantity: values.quantity,
              areaCodes: splitCommaSeparatedList(values.areaCodes),
            });

            if (numbers.length === values.quantity) {
              enqueueSnackbar(
                `Successfully acquired ${plural(
                  numbers.length,
                  "phone number",
                  "phone numbers"
                )}`,
                { variant: "success", autoHideDuration: NOTIFICATION_DURATION }
              );
            } else if (numbers.length < values.quantity) {
              if (numbers.length === 0) {
                throw new Error("No numbers were acquired");
              }

              enqueueSnackbar(
                `Successfully acquired ${plural(
                  numbers.length,
                  "phone number",
                  "phone numbers"
                )} out of ${plural(
                  values.quantity,
                  "phone number",
                  "phone numbers"
                )} requested.`,
                { variant: "warning", autoHideDuration: NOTIFICATION_DURATION }
              );
            }

            formik.resetForm();
          } catch (_e) {
            enqueueSnackbar(
              "Unable to acquire number(s) requested. Please try again, or try a different area code.",
              { variant: "error", autoHideDuration: NOTIFICATION_DURATION }
            );
            onClose();
          }
        }}
      >
        {({ isSubmitting, values, isValid, setFieldError, setFieldValue }) => {
          const selectedCampaign = availableCampaigns.find(
            ({ tcrCampaignId }) => tcrCampaignId === values.tcrCampaignId
          );

          const maxNumbersToPurchase = selectedCampaign
            ? selectedCampaign.numberLimit -
              (selectedCampaign.currentNumberCount || 0)
            : DEFAULT_MAX;
          return (
            <Form>
              <DialogContent sx={{ pb: 0 }}>
                <Typography variant="h1" pb={1}>
                  Add 10DLC Numbers
                </Typography>

                <Typography variant="body1" pb={1}>
                  Enter an area code to acquire a new 10DLC number. Note that
                  some area codes may be unavailable. You may provide multiple
                  area codes below, and we will attempt to get them in order of
                  priority.
                </Typography>

                {selectedCampaign && (
                  <Typography variant="body1" pb={1}>
                    Your 10DLC campaign has{" "}
                    {plural(
                      selectedCampaign.currentNumberCount || 0,
                      "phone number",
                      "phone numbers"
                    )}
                    , of a maximum of{" "}
                    {selectedCampaign.numberLimit || DEFAULT_MAX}.
                  </Typography>
                )}

                <FormikErrorBanner />

                {availableCampaigns.length > 1 && (
                  <FormikSelect
                    name="tcrCampaignId"
                    label="10DLC Campaign"
                    fullWidth
                  >
                    {availableCampaigns.map(availableCampaign => (
                      <MenuItem
                        key={availableCampaign.tcrCampaignId}
                        value={availableCampaign.tcrCampaignId}
                      >
                        {availableCampaign.tcrCampaignId}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                )}
                <FormikTextField
                  name="areaCodes"
                  label="Area Codes"
                  fullWidth
                  bottomLabel="Enter a comma separated list of area codes, in order of priority"
                />
                <FormikTextField
                  name="quantity"
                  label="Quantity"
                  type="number"
                  bottomLabel={`Max allowed: ${maxNumbersToPurchase}`}
                  InputProps={{
                    inputProps: { min: 1, max: maxNumbersToPurchase },
                  }}
                  onChange={e => {
                    const value = Number(e.target.value);
                    if (value > maxNumbersToPurchase) {
                      setFieldError(
                        submitErrorField,
                        `Quantity must be at most ${maxNumbersToPurchase}`
                      );
                    } else {
                      void setFieldValue("quantity", value);
                    }
                  }}
                  fullWidth
                />
              </DialogContent>

              <DialogActions>
                <Button
                  disabled={isSubmitting}
                  onClick={onClose}
                  color="secondary"
                >
                  Cancel
                </Button>

                <FormikSubmitButton
                  disabled={isSubmitting || !isValid}
                  color="primary"
                  variant="contained"
                  submitText="Add"
                  loadingText="Adding..."
                  data-testid="purchase-10dlc-numbers-button"
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
